
import { useState } from 'react';

export default function Booking() {
  const [activeTab, setActiveTab] = useState<'공연' | '체험'>('공연');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const performancePackages = [
    { name: 'Basic', duration: '80~100 만원', note: '소규모 행사', descriptions: [] as string[] },
    { name: 'Standard', duration: '110~130 만원', note: '중규모 행사', popular: true, descriptions: ['- 맞춤형 스토리 구성'] },
    { name: 'Premium', duration: '150~300 만원', note: '대규모 행사', descriptions: ['- 맞춤형 스토리 구성', '- 영상 제작'] },
  ];

  const experiencePackages = [
    { name: 'Basic', duration: '40분', note: '체험', descriptions: [] as string[] },
    { name: 'Standard', duration: '20분 + 20분', note: '공연 + 체험', popular: true, descriptions: [] as string[] },
    { name: 'Premium', duration: '40분 + 40분', note: '공연 + 체험', descriptions: [] as string[] },
  ];

  const experienceDescriptions = [
    '1인당 1개 체험 장비 구성',
    '체험 장비 1인당 2~3만원',
    '야외 체험 가능',
  ];

  const bookingSteps = [
    { step: '01', title: '문의' },
    { step: '02', title: '일정 확인' },
    { step: '03', title: '기획·견적' },
    { step: '04', title: '확정·진행' },
  ];

  const faqs = [
    { q: '야외 행사도 가능한가요?', a: '네, 조명과 전원 환경 확인 후 진행 가능합니다.' },
    { q: '최소 인원이 있나요?', a: '공연은 제한 없으며, 체험은 최소 10명부터 가능합니다.' },
    { q: '장비는 직접 준비하시나요?', a: '주어진 장소에 있는 장비를 사전에 점검 후 먼저 사용하며, 부족한 장비는 저희가 준비합니다!' },
    { q: '급한 일정도 가능한가요?', a: '일정에 따라 조율 가능합니다. 최소 1주일 전 문의 권장합니다.' },
    { q: '지역 출장도 가능한가요?', a: '네, 전국 어디든 출장 가능합니다.' },
    { q: '결제는 어떻게 하나요?', a: '계약금 입금 후 진행하며, 잔금은 행사 종료 후 정산합니다.' },
  ];

  const currentPackages = activeTab === '공연' ? performancePackages : experiencePackages;

  return (
    <section id="booking" className="py-32 lg:py-40 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">

        {/* Section Title */}
        <div className="mb-20 lg:mb-28">
          <p className="text-xs font-medium tracking-widest uppercase text-primary mb-4">Booking</p>
          <h2 className="text-3xl lg:text-5xl font-light text-black leading-tight tracking-tight">
            예약 안내
          </h2>
          <p className="text-sm text-textGray font-light mt-5 tracking-wide">
            상담 후 맞춤 견적을 안내해드립니다
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-start gap-3 mt-10">
            <a
              href="https://pf.kakao.com/_xoxmxjxb"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-[#FEE500] text-[#3A1D1D] text-sm font-medium tracking-wide rounded-md hover:bg-[#f5dc00] transition-colors cursor-pointer whitespace-nowrap"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 1.5C4.858 1.5 1.5 4.186 1.5 7.5c0 2.138 1.344 4.014 3.375 5.1L4.125 15l3.206-2.1A8.7 8.7 0 0 0 9 13.5c4.142 0 7.5-2.686 7.5-6S13.142 1.5 9 1.5Z" fill="#3A1D1D"/>
              </svg>
              카카오톡 채널 문의
            </a>
            <a
              href="tel:010-5529-0122"
              className="inline-flex items-center gap-2.5 px-6 py-3.5 border border-lineGray text-black text-sm font-light tracking-wide rounded-md hover:border-black transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-base"></i>
              010-5529-0122
            </a>
          </div>
        </div>

        {/* Package Tabs */}
        <div className="flex gap-8 mb-12 border-b border-lineGray">
          {(['공연', '체험'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-sm font-light tracking-wide pb-3 border-b-2 -mb-px transition-colors cursor-pointer ${
                activeTab === tab
                  ? 'text-black border-black'
                  : 'text-textGray border-transparent hover:text-black'
              }`}
            >
              {tab} 패키지
            </button>
          ))}
        </div>

        {/* Packages */}
        <div className="grid md:grid-cols-3 gap-px bg-lineGray mb-6">
          {currentPackages.map((pkg, index) => (
            <div
              key={index}
              className={`relative px-10 py-12 ${pkg.popular ? 'bg-black text-white' : 'bg-white'}`}
            >
              {pkg.popular && (
                <span className="absolute top-6 right-6 text-xs font-medium tracking-wider uppercase text-primary">
                  인기
                </span>
              )}
              <h3 className="text-xl font-light mb-1.5">{pkg.name}</h3>
              <p className={`text-xs font-light tracking-wide mb-8 ${pkg.popular ? 'text-white/50' : 'text-textGray'}`}>
                {pkg.note}
              </p>
              <p className="text-3xl font-light tracking-tight mb-2">{pkg.duration}</p>
              {pkg.descriptions.length > 0 && (
                <div className="flex flex-col gap-2.5 mt-8">
                  {pkg.descriptions.map((desc, i) => (
                    <p
                      key={i}
                      className={`text-sm font-light leading-relaxed px-4 py-3 ${
                        pkg.popular
                          ? 'bg-white/5 text-white/70'
                          : 'bg-neutral-50 text-textGray'
                      }`}
                    >
                      {desc}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 체험 패키지 하단 설명 */}
        {activeTab === '체험' && (
          <div className="flex flex-col gap-3 mb-24 lg:mb-32 mt-6">
            {experienceDescriptions.map((desc, i) => (
              <p
                key={i}
                className="w-full px-5 py-3.5 text-sm font-light border border-lineGray bg-neutral-50 text-textGray tracking-wide"
              >
                {desc}
              </p>
            ))}
          </div>
        )}

        {activeTab === '공연' && <div className="mb-20 lg:mb-28" />}

        {/* Booking Process */}
        <div className="mb-24 lg:mb-32">
          <p className="text-xs font-medium tracking-widest uppercase text-textGray mb-10">Process</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {bookingSteps.map((item, index) => (
              <div key={index} className="flex items-center gap-4">
                <span className="text-4xl lg:text-5xl font-extralight text-lineGray leading-none">{item.step}</span>
                <span className="text-sm font-light text-black tracking-wide">{item.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <p className="text-xs font-medium tracking-widest uppercase text-primary mb-3">FAQ</p>
          <h3 className="text-2xl lg:text-3xl font-light text-black mb-10">자주 묻는 질문</h3>

          <div className="divide-y divide-lineGray">
            {faqs.map((faq, index) => (
              <div key={index}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
                >
                  <span className="text-sm font-light text-black pr-8 leading-relaxed">{faq.q}</span>
                  <i className={`ri-${openFaqIndex === index ? 'subtract' : 'add'}-line text-lg text-textGray group-hover:text-black transition-colors flex-shrink-0`}></i>
                </button>
                {openFaqIndex === index && (
                  <div className="pb-5">
                    <p className="text-sm text-textGray font-light leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
